import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useCallback } from 'react';
import { BillingAPI } from 'api/billing';
import { getBillingVersion, getCurrentBillingCycle, getPlanId } from 'components/organisms/module-settings/billing/utils';
import { useBillingFeatureFlag } from 'hooks/use-account';
import { useTranslate } from 'hooks/use-translation';
const MODULE = 'rl_billing';
export const SUBSCRIPTION_KEY = [
    `${MODULE}_subscription`
];
export const INVOICE_DETAILS_KEY = [
    `${MODULE}_invoice_details`
];
const INVOICE_LIST_KEY = [
    `${MODULE}_invoice_list`
];
export const useSubscriptionQuery = (includeMeta = false, queryOptions = {})=>{
    const isBillingEnabled = useBillingFeatureFlag();
    return useQuery([
        SUBSCRIPTION_KEY,
        includeMeta
    ], ()=>BillingAPI.getSubscription(includeMeta), {
        ...queryOptions,
        enabled: isBillingEnabled
    });
};
export const useInvoiceDetailsQuery = (queryOptions = {})=>useQuery(INVOICE_DETAILS_KEY, ()=>BillingAPI.getInvoiceDetails(), {
        ...queryOptions
    });
export const useInvoiceListQuery = (subscriptionId, queryOptions = {})=>useQuery(INVOICE_LIST_KEY, ()=>BillingAPI.getInvoiceList(subscriptionId), {
        ...queryOptions
    });
export function useRefetchSubscriptionWithoutMeta() {
    const queryClient = useQueryClient();
    return useCallback((includeMeta)=>{
        queryClient.refetchQueries({
            predicate: (query)=>{
                var _query_queryHash;
                const { queryKey } = query;
                return queryKey[1] === includeMeta && (query === null || query === void 0 ? void 0 : (_query_queryHash = query.queryHash) === null || _query_queryHash === void 0 ? void 0 : _query_queryHash.includes(SUBSCRIPTION_KEY === null || SUBSCRIPTION_KEY === void 0 ? void 0 : SUBSCRIPTION_KEY.toString()));
            }
        });
    }, [
        queryClient
    ]);
}
const getChangePlanRequestPayload = ({ plan, form, subscription })=>{
    var _ref, _ref1;
    const planId = getPlanId(plan, (_ref = form === null || form === void 0 ? void 0 : form.billing_cycle) !== null && _ref !== void 0 ? _ref : getCurrentBillingCycle(subscription.plan.planName));
    const payload = {
        plan: {
            planId,
            quantity: (_ref1 = form === null || form === void 0 ? void 0 : form.quantity) !== null && _ref1 !== void 0 ? _ref1 : subscription.licenseQuantity
        },
        subscriptionId: subscription.subscriptionId
    };
    return payload;
};
const changePlanMutationFn = (changePlanParams)=>{
    return BillingAPI.changePlan(getChangePlanRequestPayload(changePlanParams));
};
const downgradePlanMutationFn = (changePlanParams)=>{
    return BillingAPI.changePlan({
        ...getChangePlanRequestPayload(changePlanParams),
        hasScheduledChanges: true
    });
};
const changePlanCheckoutMutationFn = ({ plan, form, subscription, isReactivation })=>{
    var _ref, _ref1;
    const planId = getPlanId(plan, (_ref = form === null || form === void 0 ? void 0 : form.billing_cycle) !== null && _ref !== void 0 ? _ref : getCurrentBillingCycle(subscription.plan.planName));
    const payload = {
        plan: {
            planId,
            quantity: (_ref1 = form === null || form === void 0 ? void 0 : form.quantity) !== null && _ref1 !== void 0 ? _ref1 : subscription.licenseQuantity
        },
        subscriptionId: subscription.subscriptionId
    };
    return isReactivation ? BillingAPI.reactivationCheckout(payload) : BillingAPI.changePlanCheckout(payload);
};
const changeAddonCheckoutMutationFn = ({ count, addonIdentifier })=>{
    return BillingAPI.changeAddonCheckout(count, addonIdentifier);
};
const changePlanEstimateMutationFn = ({ plan, form, subscription })=>{
    var _ref, _ref1;
    const planId = getPlanId(plan, (_ref = form === null || form === void 0 ? void 0 : form.billing_cycle) !== null && _ref !== void 0 ? _ref : getCurrentBillingCycle(subscription.plan.planName));
    const payload = {
        plan: {
            planId,
            quantity: (_ref1 = form === null || form === void 0 ? void 0 : form.quantity) !== null && _ref1 !== void 0 ? _ref1 : subscription.licenseQuantity
        },
        subscriptionId: subscription.subscriptionId
    };
    return BillingAPI.changePlanEstimate(payload);
};
const updateInvoiceDetailsMutationFn = (payload)=>{
    return BillingAPI.updateInvoiceDetails(payload);
};
export const useChangePlanMutation = (options = {})=>useMutation(changePlanMutationFn, options);
export const useDowngradePlanMutation = (options = {})=>useMutation(downgradePlanMutationFn, options);
export const useChangePlanEstimateMutation = (options = {})=>useMutation(changePlanEstimateMutationFn, options);
export const useChangePlanCheckoutMutation = (options = {})=>useMutation(changePlanCheckoutMutationFn, options);
export const useChangeAddonCheckoutMutation = (options = {})=>useMutation(changeAddonCheckoutMutationFn, options);
const addonUsageDetailsMutationFn = ({ feature })=>{
    return BillingAPI.getAddonUsageDetails({
        feature
    });
};
export const useGetAddonUsageDetails = (options = {})=>useMutation(addonUsageDetailsMutationFn, options);
export const useCreatePortalSessionMutation = (options = {})=>useMutation(()=>BillingAPI.createPortalSession(), options);
export const useRemoveScheduledPlanChangeMutation = (options = {})=>useMutation(()=>BillingAPI.removeScheduledPlanChange(), options);
export const useScheduleSubscriptionCancellationMutation = (options = {})=>useMutation((formValues)=>BillingAPI.scheduleSubscriptionCancellation(formValues), options);
export const useRemoveScheduledCancellationMutation = (options = {})=>useMutation(()=>BillingAPI.removeScheduledCancellation(), options);
export const useUpdateInvoiceDetailsMutation = (options = {})=>useMutation(updateInvoiceDetailsMutationFn, options);
const addLicensesMutationFn = (count)=>{
    return BillingAPI.addLicences(count);
};
const addAddonLicensesMutationFn = ({ count, addonIdentifier })=>{
    return BillingAPI.addLicenceAddon(count, addonIdentifier);
};
const addAddonLicensesEstimateMutationFn = ({ count, addonIdentifier })=>{
    return BillingAPI.addLicenceAddonEstimate(count, addonIdentifier);
};
const addLicensesEstimateMutationFn = (count)=>{
    return BillingAPI.addLicencesEstimate(count);
};
export const useAddAddonLicensesMutation = (options = {})=>useMutation(addAddonLicensesMutationFn, options);
export const useAddLicensesMutation = (options = {})=>useMutation(addLicensesMutationFn, options);
export const useAddLicensesEstimateMutation = (options = {})=>useMutation(addLicensesEstimateMutationFn, options);
export const useAddAddonLicensesEstimateMutation = (options = {})=>useMutation(addAddonLicensesEstimateMutationFn, options);
const forceSyncSubscriptionMutationFn = (subscriptionId)=>{
    return BillingAPI.forceSyncSubscription(subscriptionId);
};
export const useForceSyncSubscriptionMutation = (options = {})=>useMutation(forceSyncSubscriptionMutationFn, options);
const addMembersMutationFn = (payload)=>{
    return BillingAPI.addMembers(payload);
};
export const useAddMembersMutation = (options = {})=>useMutation(addMembersMutationFn, options);
const addMemberAddonMutationFn = ({ data, addonIdentifier })=>{
    return BillingAPI.addMembersAddon(data, addonIdentifier);
};
export const useAddMemberAddonMutuation = (options = {})=>useMutation(addMemberAddonMutationFn, options);
const addMemberAddonEstMutationFn = ({ data, addonIdentifier })=>{
    return BillingAPI.estimateAddMembersAddon(data, addonIdentifier);
};
export const useAddMemberAddonEstMutuation = (options = {})=>useMutation(addMemberAddonEstMutationFn, options);
const addMembersEstimateMutationFn = (payload)=>{
    return BillingAPI.addMembersEstimate(payload);
};
export const useAddMembersEstimateMutation = (options = {})=>useMutation(addMembersEstimateMutationFn, options);
export const useAddPartnerMembersEstimateMutation = (options = {})=>useMutation((payload)=>BillingAPI.addPartnerMembersEstimate(payload), options);
const reactivateMemberMutationFn = (userId)=>{
    return BillingAPI.reactivateMember(userId);
};
export const useReactivateMemberMutation = (options = {})=>useMutation(reactivateMemberMutationFn, options);
const reactivateMemberEstimateMutationFn = (userId)=>{
    return BillingAPI.reactivateMemberEstimate(userId);
};
export const useReactivateMemberEstimateMutation = (options = {})=>useMutation(reactivateMemberEstimateMutationFn, options);
export const useReactivatePartnerMemberEstimateMutation = (options = {})=>useMutation((userId)=>BillingAPI.reactivatePartnerMemberEstimate(userId), options);
export const useAvailableLicensesQuery = ()=>{
    const { data: subscription } = useSubscriptionQuery();
    const availableLicenses = useMemo(()=>{
        var _ref;
        return subscription ? (_ref = subscription === null || subscription === void 0 ? void 0 : subscription.availableUserLicenseQuantity) !== null && _ref !== void 0 ? _ref : 0 : 0;
    }, [
        subscription
    ]);
    return availableLicenses;
};
export const useAvailablePartnerLicensesQuery = ()=>{
    const { data: subscription } = useSubscriptionQuery(true);
    // If account has partner addon, get the availablePartnerLicenses else get the userLicenses
    const availablePartnerLicenses = useMemo(()=>{
        var _ref, _ref1, _ref2;
        var _subscription_subscriptionMeta;
        return ((_ref = subscription === null || subscription === void 0 ? void 0 : (_subscription_subscriptionMeta = subscription.subscriptionMeta) === null || _subscription_subscriptionMeta === void 0 ? void 0 : _subscription_subscriptionMeta.hasPartnerAddon) !== null && _ref !== void 0 ? _ref : false) ? (_ref1 = subscription === null || subscription === void 0 ? void 0 : subscription.availablePartnerLicenseQuantity) !== null && _ref1 !== void 0 ? _ref1 : 0 : (_ref2 = subscription === null || subscription === void 0 ? void 0 : subscription.availableUserLicenseQuantity) !== null && _ref2 !== void 0 ? _ref2 : 0;
    }, [
        subscription
    ]);
    return availablePartnerLicenses;
};
export const useAvailableClientPartnerLicensesQuery = ()=>{
    const { data: subscription } = useSubscriptionQuery(true);
    // If account has partner addon, get the availablePartnerLicenses else get the userLicenses
    const availableExternalPartnerLicenses = useMemo(()=>{
        var _ref, _ref1, _ref2;
        var _subscription_subscriptionMeta;
        return ((_ref = subscription === null || subscription === void 0 ? void 0 : (_subscription_subscriptionMeta = subscription.subscriptionMeta) === null || _subscription_subscriptionMeta === void 0 ? void 0 : _subscription_subscriptionMeta.hasExternalPartnerAddon) !== null && _ref !== void 0 ? _ref : false) ? (_ref1 = subscription === null || subscription === void 0 ? void 0 : subscription.availableExternalPartnerLicenseQuantity) !== null && _ref1 !== void 0 ? _ref1 : 0 : (_ref2 = subscription === null || subscription === void 0 ? void 0 : subscription.availableUserLicenseQuantity) !== null && _ref2 !== void 0 ? _ref2 : 0;
    }, [
        subscription
    ]);
    return availableExternalPartnerLicenses;
};
export const useHasPartnerAddon = ()=>{
    const { data: subscription } = useSubscriptionQuery(true);
    const hasPartnerAddon = useMemo(()=>{
        var _ref;
        var _subscription_subscriptionMeta;
        return (_ref = subscription === null || subscription === void 0 ? void 0 : (_subscription_subscriptionMeta = subscription.subscriptionMeta) === null || _subscription_subscriptionMeta === void 0 ? void 0 : _subscription_subscriptionMeta.hasPartnerAddon) !== null && _ref !== void 0 ? _ref : false;
    }, [
        subscription
    ]);
    return hasPartnerAddon;
};
export const useBillingVersion = ()=>{
    const { data: subscription } = useSubscriptionQuery();
    const version = useMemo(()=>subscription ? getBillingVersion(subscription === null || subscription === void 0 ? void 0 : subscription.plan.planId) : null, [
        subscription
    ]);
    return version;
};
export const usePlanName = (plan)=>{
    const translate = useTranslate();
    return useMemo(()=>{
        if (!plan) {
            return '';
        }
        const planKey = `billing.plan.${plan}`.toLowerCase();
        return translate(planKey);
    }, [
        plan,
        translate
    ]);
};
